/**
 * ============================================================================
 * CALENDAR UTILS - TIMEZONE FIXED
 * ============================================================================
 * Hilfsfunktionen und Konstanten für das Kalender-Modul
 * 
 * @version 1.3.0 - FIXED: Timezone-korrekte Date-Verarbeitung
 */

// ===========================
// CONSTANTS
// ===========================

export const weekDays = [
  'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 
  'Freitag', 'Samstag', 'Sonntag'
];

export const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

export const categories = [
  { id: 'arzt', name: '⚕️ Arzt', color: '#f44336' },
  { id: 'schule', name: '🎓 Schule', color: '#2196f3' },
  { id: 'privat', name: '🏠 Privat', color: '#4caf50' },
  { id: 'arbeit', name: '💼 Arbeit', color: '#ff9800' }
];

export const recurringOptions = [
  { id: 'none', label: 'Einmalig' },
  { id: 'daily', label: 'Täglich' },
  { id: 'weekly', label: 'Wöchentlich' },
  { id: 'monthly', label: 'Monatlich' },
  { id: 'yearly', label: 'Jährlich' }
];

export const familyMembers = [
  { id: 1, name: 'Thomas', avatar: '👨' },
  { id: 2, name: 'Maria', avatar: '👩' },
  { id: 3, name: 'Emma', avatar: '👧' },
  { id: 4, name: 'Luca', avatar: '👦' },
  { id: 5, name: 'Sophie', avatar: '👶' }
];

// ===========================
// DATE UTILITY FUNCTIONS
// ===========================

/**
 * ✅ CRITICAL FIX: Parse Date String LOKAL (ohne UTC Konvertierung)
 * "2025-11-02" → Date Object mit lokalem Midnight, NICHT UTC
 */
export function parseLocalDate(dateStr) {
  if (!dateStr) return new Date();
  
  // Normalisiere zuerst (entferne Timestamp falls vorhanden)
  const normalized = dateStr.split('T')[0];
  
  // Parse als lokales Datum (YYYY-MM-DD)
  const [year, month, day] = normalized.split('-').map(Number);
  
  // ✅ Erstelle Date mit lokaler Timezone (nicht UTC!)
  return new Date(year, month - 1, day);
}

export function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

export function getEndOfWeek(date) {
  const d = new Date(getStartOfWeek(date));
  d.setDate(d.getDate() + 6);
  return d;
}

export function getStartOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function getEndOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function getDaysInMonth(date) {
  const firstDay = getStartOfMonth(date);
  const lastDay = getEndOfMonth(date);
  const days = [];
  
  // Füge Tage vom vorherigen Monat hinzu (für vollständige Wochen)
  const firstDayOfWeek = firstDay.getDay() || 7; // Montag = 1
  const prevMonthDays = firstDayOfWeek - 1;
  
  for (let i = prevMonthDays; i > 0; i--) {
    const d = new Date(firstDay);
    d.setDate(d.getDate() - i);
    days.push({ date: d, isCurrentMonth: false });
  }
  
  // Füge Tage des aktuellen Monats hinzu
  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
    days.push({ date: new Date(d), isCurrentMonth: true });
  }
  
  // Füge Tage vom nächsten Monat hinzu (für vollständige Wochen)
  const remaining = 42 - days.length; // 6 Wochen * 7 Tage
  for (let i = 1; i <= remaining; i++) {
    const d = new Date(lastDay);
    d.setDate(d.getDate() + i);
    days.push({ date: d, isCurrentMonth: false });
  }
  
  return days;
}

export function getCurrentWeekDays(currentDate) {
  const days = [];
  const start = getStartOfWeek(currentDate);
  for (let i = 0; i < 7; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    days.push(day);
  }
  return days;
}

export function getWeekNumber(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// ===========================
// FORMATTING FUNCTIONS
// ===========================

export function formatDate(date) {
  return `${date.getDate()}.${date.getMonth() + 1}.`;
}

export function formatFullDate(date) {
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
}

export function formatInputDate(date) {
  // ✅ FIXED: Verwende lokale Datums-Komponenten (nicht UTC!)
  // ✅ v8.0: Akzeptiert sowohl Date-Objekte als auch Strings

  // Wenn bereits ein String im richtigen Format
  if (typeof date === 'string') {
    // Falls bereits YYYY-MM-DD Format
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // Versuche String zu Date zu konvertieren
    date = new Date(date);
  }

  // Prüfe ob gültiges Date-Objekt
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    console.warn('formatInputDate: Invalid date received:', date);
    // Fallback: Heutiges Datum
    date = new Date();
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * ✅ v7.3 FIX: Normalisiert Datum zu "YYYY-MM-DD" Format
 * Konvertiert ISO Timestamp korrekt von UTC zu lokaler Zeit
 */
export function normalizeDateString(dateStr) {
  if (!dateStr) return dateStr;

  // Wenn ISO Timestamp: "2025-11-02T00:00:00.000Z" → parse als Date und konvertiere zu lokal
  if (dateStr.includes('T') || dateStr.includes('Z')) {
    const d = new Date(dateStr);
    // Verwende lokale Datums-Komponenten (nicht UTC!)
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Bereits im richtigen Format (YYYY-MM-DD String)
  return dateStr;
}

// ===========================
// COMPARISON FUNCTIONS
// ===========================

export function isToday(date) {
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

export function isSameDay(date1, date2) {
  return date1.toDateString() === date2.toDateString();
}

// ===========================
// APPOINTMENT FUNCTIONS
// ===========================

export function getAppointmentsForDay(appointments, date) {
  const dateStr = formatInputDate(date);
  
  console.log('========================================');
  console.log('🔍 getAppointmentsForDay DEBUG (TIMEZONE FIXED)');
  console.log('========================================');
  console.log('📅 Looking for date:', dateStr);
  console.log('📅 Date object:', date);
  console.log('📅 Date components:', {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    dayName: weekDays[date.getDay() === 0 ? 6 : date.getDay() - 1]
  });
  console.log('📊 Total appointments received:', appointments.length);
  console.log('========================================');
  
  if (appointments.length === 0) {
    console.log('⚠️ NO APPOINTMENTS to filter!');
    console.log('========================================');
    return [];
  }
  
  // ✅ Filter mit normalisiertem Datum
  const result = appointments.filter(apt => {
    const aptDate = normalizeDateString(apt.date);
    const matches = aptDate === dateStr;
    
    console.log(`📌 ${apt.title}:`, {
      original: apt.date,
      normalized: aptDate,
      looking: dateStr,
      matches: matches ? '✅ MATCH' : '❌ no match'
    });
    
    return matches;
  });
  
  console.log('========================================');
  console.log('✅ RESULT:', result.length, 'appointments found for', dateStr);
  if (result.length > 0) {
    console.log('🎉 Found:', result.map(a => `${a.title} at ${a.time}`));
  } else {
    console.log('❌ NO MATCHES!');
    console.log('💡 Available dates:', [...new Set(appointments.map(a => normalizeDateString(a.date)))]);
  }
  console.log('========================================');
  
  return result;
}

export function getAppointmentsForDateRange(appointments, startDate, endDate) {
  const start = formatInputDate(startDate);
  const end = formatInputDate(endDate);
  
  console.log('📅 getAppointmentsForDateRange:', {
    start,
    end,
    totalAppointments: appointments.length
  });
  
  // ✅ Normalisiere Dates vor Vergleich
  const result = appointments
    .map(apt => ({
      ...apt,
      date: normalizeDateString(apt.date)
    }))
    .filter(apt => apt.date >= start && apt.date <= end)
    .sort((a, b) => {
      if (a.date === b.date) {
        return a.time.localeCompare(b.time);
      }
      return a.date.localeCompare(b.date);
    });
    
  console.log('📅 Found in range:', result.length);
  
  return result;
}

export function getCategoryInfo(categoryId) {
  return categories.find(c => c.id === categoryId) || categories[0];
}

export function getFamilyMember(personName) {
  return familyMembers.find(m => m.name.toLowerCase() === personName?.toLowerCase());
}

// ===========================
// RECURRING APPOINTMENTS
// ===========================

/**
 * ✅ TIMEZONE FIXED: Wiederholende Termine generieren
 * Verwendet parseLocalDate für korrekte Datums-Berechnung
 */
export function generateRecurringAppointments(baseAppointments) {
  console.log('========================================');
  console.log('🔄 generateRecurringAppointments START (TIMEZONE FIXED)');
  console.log('========================================');
  console.log('🔄 Base appointments:', baseAppointments.length);
  
  const generated = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to midnight
  
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 365); // 1 Jahr voraus

  baseAppointments.forEach((apt, index) => {
    console.log(`🔄 Processing appointment ${index + 1}:`, {
      id: apt.id,
      title: apt.title,
      date: apt.date,
      recurring: apt.recurring || 'none'
    });
    
    // ✅ CRITICAL: Normalisiere Datum
    const normalizedDate = normalizeDateString(apt.date);
    
    if (!apt.recurring || apt.recurring === 'none') {
      // ✅ Einmaliger Termin - MIT normalisiertem Datum
      generated.push({
        ...apt,
        date: normalizedDate
      });
      console.log('   ✅ Added as single appointment:', normalizedDate);
    } else {
      // ✅ Wiederholender Termin - MIT LOKALER Datums-Berechnung
      const startDate = parseLocalDate(normalizedDate);
      let currentDate = new Date(startDate);
      let count = 0;

      console.log('   🔄 Generating recurring instances...');
      console.log('      Start date:', startDate.toDateString());
      console.log('      Pattern:', apt.recurring);

      while (currentDate <= maxDate && count < 100) { // Safety limit
        // Nur wenn nach Startdatum
        if (currentDate >= startDate) {
          const dateStr = formatInputDate(currentDate);
          generated.push({
            ...apt,
            id: `${apt.id}-${dateStr}`,
            date: dateStr,  // ✅ Immer "YYYY-MM-DD"
            isRecurring: true,
            originalId: apt.id
          });
          count++;
          
          if (count <= 3) {
            console.log(`      Instance ${count}:`, dateStr, currentDate.toDateString());
          }
        }

        // Nächstes Datum berechnen
        switch (apt.recurring) {
          case 'daily':
            currentDate.setDate(currentDate.getDate() + 1);
            break;
          case 'weekly':
            currentDate.setDate(currentDate.getDate() + 7);
            break;
          case 'monthly':
            currentDate.setMonth(currentDate.getMonth() + 1);
            break;
          case 'yearly':
            currentDate.setFullYear(currentDate.getFullYear() + 1);
            break;
          default:
            currentDate = new Date(maxDate.getTime() + 1); // Exit loop
        }
      }
      
      console.log(`   ✅ Generated ${count} recurring instances`);
    }
  });

  console.log('========================================');
  console.log('🔄 GENERATION COMPLETE');
  console.log('========================================');
  console.log('✅ Total appointments:', generated.length);
  
  // Group by date for debugging
  const byDate = {};
  generated.forEach(apt => {
    const date = apt.date;
    if (!byDate[date]) byDate[date] = [];
    byDate[date].push(apt.title);
  });
  
  console.log('✅ Appointments by date:', byDate);
  console.log('========================================');
  
  return generated;
}